import React from 'react';
import { Dispatch } from 'redux';
import { ForeignBusinessSponsorshipItemModel } from 'forms/sections/foreign/business/sponsorship/types';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
  CommonAddressBooks,
} from 'types/props';
import {
  CountryArrayModel, CountryModel, DateModel, LocationModel
} from 'forms/shared/types';
import ValidationElement from 'components/Form/ValidationElement';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import Name from 'components/Form/Name';
import NotApplicable from 'components/Form/NotApplicable';
import DateControl from 'components/Form/DateControl';
import Country from 'components/Form/Country';
import DateRange from 'components/Form/DateRange';
import Textarea from 'components/Form/Textarea';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import { isPVQForm } from 'helpers/form';
import RadioGroup from 'components/Form/RadioGroup';
import PVQName from 'components/Form/PVQName';
import PVQLocation from 'components/Form/PVQLocation';
import PVQLocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import { foreignBusinessAffairsSponsorshipReasonOptions } from 'constants/enums/foreignBusinessOptions';
import Radio from 'components/Form/Radio';
import Branch from 'components/Form/Branch';
import { isSafeInternational } from 'helpers/locations';
import {

  ApplicableModel

} from 'forms/shared/types';
import { pickDate } from '../../../../validators/helpers';

interface Props extends Partial<ForeignBusinessSponsorshipItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks

  applicantBirthdate?: DateModel
  dispatch?: Dispatch
  formType?: string
}

export default class SponsorshipItem extends ValidationElement<Props> {
  handleNameError = (value: any, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<ForeignBusinessSponsorshipItemModel>) => {
    this.props.onUpdate({
      Name: this.props.Name,
      BirthdateNotApplicable: this.props.BirthdateNotApplicable,
      Birthdate: this.props.Birthdate,
      Birthplace: this.props.Birthplace,
      Address: this.props.Address,
      Citizenship: this.props.Citizenship,
      OrganizationNotApplicable: this.props.OrganizationNotApplicable,
      Organization: this.props.Organization,
      Dates: this.props.Dates,
      Residence: this.props.Residence,
      Stay: this.props.Stay,
      Sponsorship: this.props.Sponsorship,
      OrganizationAddressNotApplicable: this.props.OrganizationAddressNotApplicable,
      OrganizationAddress: this.props.OrganizationAddress,
      AddressNotApplicable: this.props.AddressNotApplicable, // PVQ Only
      WorkedWithPrivateOrganization: this.props.WorkedWithPrivateOrganization,
      Reason: this.props.Reason, // PVQ Only
      ReasonOtherExplanation: this.props.ReasonOtherExplanation, // PVQ Only
      InUsaNotApplicable: this.props.InUsaNotApplicable,
      ...queue,
    });
  };

  updateName = (values: ForeignBusinessSponsorshipItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateBirthdateNotApplicable = (
    values: ForeignBusinessSponsorshipItemModel['BirthdateNotApplicable']
  ) => {
    this.update({
      Birthdate: values.applicable ? this.props.Birthdate : {},
      BirthdateNotApplicable: values,
    });
  };

  updateBirthdate = (values: ForeignBusinessSponsorshipItemModel['Birthdate']) => {
    this.update({
      Birthdate: values,
    });
  };

  updateBirthplace = (values: ForeignBusinessSponsorshipItemModel['Birthplace']) => {
    this.update({
      Birthplace: values,
    });
  };

  updateAddress = (values: ForeignBusinessSponsorshipItemModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateCitizenship = (values: ForeignBusinessSponsorshipItemModel['Citizenship']) => {
    this.update({
      Citizenship: values,
    });
  };

  updateOrganizationNotApplicable = (
    values: ForeignBusinessSponsorshipItemModel['OrganizationNotApplicable']
  ) => {
    this.update({
      Organization: values.applicable ? this.props.Organization : {},
      OrganizationNotApplicable: values,
    });
  };

  updateOrganization = (values: ForeignBusinessSponsorshipItemModel['Organization']) => {
    this.update({
      Organization: values,
    });
  };

  updateDates = (values: ForeignBusinessSponsorshipItemModel['Dates']) => {
    this.update({
      Dates: values,
    });
  };

  updateResidence = (values: ForeignBusinessSponsorshipItemModel['Residence']) => {
    this.update({
      Residence: values,
    });
  };

  updateStay = (values: ForeignBusinessSponsorshipItemModel['Stay']) => {
    this.update({
      Stay: values,
    });
  };

  updateSponsorship = (values: ForeignBusinessSponsorshipItemModel['Sponsorship']) => {
    this.update({
      Sponsorship: values,
    });
  };

  updateOrganizationAddressNotApplicable = (
    values: ForeignBusinessSponsorshipItemModel['OrganizationAddressNotApplicable']
  ) => {
    this.update({
      OrganizationAddress: values.applicable ? this.props.OrganizationAddress : {},
      OrganizationAddressNotApplicable: values,
    });
  };

  updateOrganizationAddress = (
    values: ForeignBusinessSponsorshipItemModel['OrganizationAddress']
  ) => {
    this.update({
      OrganizationAddress: values,
    });
  };

  updateReason = (value: ForeignBusinessSponsorshipItemModel['Reason']) => {
    this.update({
      Reason: value,
      ReasonOtherExplanation: value?.value === 'Other' ? this.props.ReasonOtherExplanation : {},
    });
  };

  updateReasonOtherExplanation = (
    values: ForeignBusinessSponsorshipItemModel['ReasonOtherExplanation']
  ) => {
    this.update({
      ReasonOtherExplanation: values,
    });
  };

  updateAddressNotApplicable = (
    addressNotApplicable: ForeignBusinessSponsorshipItemModel['AddressNotApplicable']
  ) => {
    const queue: Partial<ForeignBusinessSponsorshipItemModel> = { AddressNotApplicable: addressNotApplicable };

    if (addressNotApplicable?.applicable === false) {
      const address: LocationModel | undefined = this.props.Address;

      let country: CountryModel | string | CountryArrayModel | undefined = address?.country;

      if (country) {
        if (isSafeInternational(address)) {
          country = { value: 'INTERNATIONAL' } as CountryModel;
        }

        queue.Address = { country } as LocationModel;
      } else {
        queue.Address = {} as LocationModel;
      }
    }

    this.update(queue);
  };

  updateWorkedWithPrivateOrganization = (values: ForeignBusinessPoliticalItemModel['WorkedWithPrivateOrganization']) => {
    this.update({
      WorkedWithPrivateOrganization: values,
      Organization: values?.value === 'Yes' ? this.props.Organization : {},
      OrganizationAddress: values?.value === 'Yes' ? this.props.OrganizationAddress : {},
    });
  };

  updateInUsaNotApplicable = (
    inUsaNotApplicable: ApplicableModel
  ) => {
    const queue: Partial<ForeignBusinessSponsorshipItemModel> = {
      InUsaNotApplicable: { applicable: inUsaNotApplicable.applicable }
    };

    if (!inUsaNotApplicable.applicable) {
      queue.Address = {} as LocationModel;
    }

    this.update(queue);
  };

  render() {
    const sponsorDOBMinDate = pickDate([this.props.applicantBirthdate, this.props.Birthdate]);
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div className="sponsorship-item">
        <Field
          title={i18n.t('foreign.pvq.business.sponsorship.heading.reason')}
          className="reason"
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Reason`}
        >
          <RadioGroup
            className="reason option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValue={this.props.Reason?.value}
            attribute={`${attribute}.Reason`}
          >
            {foreignBusinessAffairsSponsorshipReasonOptions.map((option) => (
              <Radio
                key={option}
                name={option}
                label={option}
                value={option}
                className={option}
                onUpdate={this.updateReason}
                onError={this.props.onError}
              />
            ))}
          </RadioGroup>
        </Field>

        {this.props.Reason?.value === 'Other'
          && (
            <Field
              title={i18n.t('foreign.pvq.business.sponsorship.heading.explain')}
              optional
              filterErrors={Name.requiredErrorsOnly}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ReasonOtherExplanation`}
            >
              <Textarea
                {...this.props.ReasonOtherExplanation}
                name="ReasonOtherExplanation"
                onUpdate={this.updateReasonOtherExplanation}
                onError={this.props.onError}
                className="foreign-business-sponsorship-stay"
                required={this.props.required}
                attribute={`${attribute}.ReasonOtherExplanation`}
              />
            </Field>
          )}

        <Field
          title={i18n.t('foreign.pvq.business.sponsorship.heading.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQName
            {...this.props.Name}
            name="Name"
            aria-label={i18n.t('foreign.pvq.business.sponsorship.heading.name')}
            onUpdate={this.updateName}
            onError={this.handleNameError}
            className="foreign-business-sponsorship-name"
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Name`}
            showLastFirstMiddleNameOrder
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.sponsorship.heading.birthdate')}
          help="foreign.pvq.business.sponsorship.help.birthdate"
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Birthdate`}
        >
          <NotApplicable
            {...this.props.BirthdateNotApplicable}
            name="BirthdateNotApplicable"
            label={i18n.t('foreign.pvq.business.sponsorship.label.idk')}
            or={i18n.m('foreign.pvq.business.sponsorship.para.or')}
            onUpdate={this.updateBirthdateNotApplicable}
            onError={this.props.onError}
            className="foreign-business-sponsorship-birthdate-na"
            required={this.props.required}
            attribute={`${attribute}.BirthdateNotApplicable`}
          >
            <DateControl
              name="Birthdate"
              {...this.props.Birthdate}
              onUpdate={this.updateBirthdate}
              onError={this.props.onError}
              className="foreign-business-sponsorship-birthdate"
              required={this.props.required}
              attribute={`${attribute}.Birthdate`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.sponsorship.heading.birthplace')}
          adjustFor="birthplace"
          validate={false}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Birthplace`}
        >
          <Location
            {...this.props.Birthplace}
            name="Birthplace"
            onUpdate={this.updateBirthplace}
            onError={this.props.onError}
            layout={Location.CITY_COUNTRY}
            label={i18n.t('foreign.pvq.business.sponsorship.label.birthplace')}
            className="foreign-business-sponsorship-birthplace"
            addressBooks={this.props.addressBooks}
            addressBook="ForeignNational"
            dispatch={this.props.dispatch}
            required={this.props.required}
            attribute={`${attribute}.Birthplace`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.sponsorship.heading.citizenship')}
          help="foreign.pvq.business.sponsorship.help.citizenship"
          adjustFor="country"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Citizenship`}
        >
          <Country
            name="Citizenship"
            {...this.props.Citizenship}
            onUpdate={this.updateCitizenship}
            onError={this.props.onError}
            className="foreign-business-sponsorship-citizenship"
            multiple
            required={this.props.required}
            attribute={`${attribute}.Citizenship`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.sponsorship.heading.dates')}
          help="foreign.pvq.business.sponsorship.help.dates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Dates`}
        >
          <DateRange
            {...this.props.Dates}
            name="Dates"
            onUpdate={this.updateDates}
            minDate={sponsorDOBMinDate}
            minDateEqualTo
            onError={this.props.onError}
            className="foreign-business-sponsorship-dates"
            required={this.props.required}
            prefix="foreignNationalSponsorship"
            hideFromDay
            hideToDay
            attribute={`${attribute}.Dates`}
          />
        </Field>

        <Field
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Address`}
          adjustFor="address"
        >
          <PVQLocationUnknown
            {...this.props.Address}
            addressUnknown={this.props.AddressNotApplicable}
            addressUnknownLabel={i18n.t('foreign.pvq.business.ventures.label.idk')}
            inUsaUnknown={this.props.InUsaNotApplicable}
            inUsaUnknownLabel={i18n.t('reference.label.idk')}
            name="Address"
            onUpdate={this.updateAddress}
            onUpdateAddressUnknown={this.updateAddressNotApplicable}
            onUpdateInUsaUnknown={this.updateInUsaNotApplicable}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            onError={this.props.onError}
            className="foreign-business-sponsorship-address"
            layout={Location.ADDRESS}
            geocode
            showAddressUnknown
            showCityUnknown
            showZipcodeUnknown
            showStreetUnknown
            showPostOffice
            showMilitaryInstallation={this.props.Address?.country === 'United States' || this.props.Address?.country?.value === 'United States'}
            showInUsaUnknown
            hideInstallationNameUnknownExplanation
            hideInstallationNameUnknown
            showIsInstallationUnknownIfInternational
            showIsInstallationUnknownIfDomestic
            showInternationalStreet={false}
            addressBooks={this.props.addressBooks}
            addressBook="ForeignNational"
            dispatch={this.props.dispatch}
            required={this.props.required}
            attribute={`${attribute}.Address`}
            label={i18n.t(`foreign.pvq.business.sponsorship.heading.address`)}
            domesticTitle={i18n.t(`foreign.pvq.business.sponsorship.heading.addressDomesticTitle`)}
            foreignTitle={i18n.t(`foreign.pvq.business.sponsorship.heading.addressForeignTitle`)}
          />
        </Field>

        <Branch
          {...this.props.WorkedWithPrivateOrganization}
          name="WorkedWithPrivateOrganization"
          attribute={`${attribute}.WorkedWithPrivateOrganization`}
          label={i18n.t('foreign.pvq.business.sponsorship.heading.workedWithPrivateOrganization')}
          labelSize="h4"
          className="worked-with-private-organization"
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateWorkedWithPrivateOrganization}
          scrollIntoView={this.props.scrollIntoView}
        />

        {this.props.WorkedWithPrivateOrganization?.value === 'Yes' && (
          <>
            <Field
              title={i18n.t('foreign.pvq.business.sponsorship.heading.organization')}
              adjustFor="text"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Organization`}
              exactAttribute
            >
              <Text
                name="Organization"
                {...this.props.Organization}
                onUpdate={this.updateOrganization}
                onError={this.props.onError}
                className="foreign-business-sponsorship-organization"
                required={this.props.required}
                attribute={`${attribute}.Organization`}
              />
            </Field>

            <Field
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.OrganizationAddress`}
              adjustFor="address"
            >
              <PVQLocation
                name="OrganizationAddress"
                {...this.props.OrganizationAddress}
                onUpdate={this.updateOrganizationAddress}
                onError={this.props.onError}
                className="foreign-business-sponsorship-organizationaddress"
                layout={Location.ADDRESS}
                geocode
                showInternationalStreet={false}
                addressBooks={this.props.addressBooks}
                addressBook="Organization"
                dispatch={this.props.dispatch}
                required={this.props.required}
                attribute={`${attribute}.OrganizationAddress`}
                label={i18n.t(`foreign.pvq.business.sponsorship.heading.organizationAddress`)}
                domesticTitle={i18n.t(`foreign.pvq.business.sponsorship.heading.organizationAddressDomesticTitle`)}
                foreignTitle={i18n.t(`foreign.pvq.business.sponsorship.heading.organizationAddressForeignTitle`)}
              />
            </Field>
          </>
        )}
      </div>
    ) : (
      <div className="sponsorship-item">
        <Field
          title={i18n.t('foreign.business.sponsorship.heading.name')}
          optional
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Name
            name="Name"
            {...this.props.Name}
            aria-label="Provide the name of the sponsored foreign national"
            onUpdate={this.updateName}
            onError={this.handleNameError}
            className="foreign-business-sponsorship-name"
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Name`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.sponsorship.heading.birthdate')}
          help="foreign.business.sponsorship.help.birthdate"
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Birthdate`}
        >
          <NotApplicable
            name="BirthdateNotApplicable"
            label={i18n.t('foreign.business.sponsorship.label.idk')}
            or={i18n.m('foreign.business.sponsorship.para.or')}
            {...this.props.BirthdateNotApplicable}
            onUpdate={this.updateBirthdateNotApplicable}
            onError={this.props.onError}
            className="foreign-business-sponsorship-birthdate-na"
            required={this.props.required}
            attribute={`${attribute}.BirthdateNotApplicable`}
          >
            <DateControl
              name="Birthdate"
              {...this.props.Birthdate}
              relationship="Other"
              onUpdate={this.updateBirthdate}
              onError={this.props.onError}
              className="foreign-business-sponsorship-birthdate"
              required={this.props.required}
              hideDay
              attribute={`${attribute}.Birthdate`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('foreign.business.sponsorship.heading.birthplace')}
          adjustFor="birthplace"
          validate={false}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Birthplace`}
        >
          <Location
            name="Birthplace"
            {...this.props.Birthplace}
            onUpdate={this.updateBirthplace}
            onError={this.props.onError}
            layout={Location.US_CITY_STATE_ZIP_INTERNATIONAL_CITY}
            label={i18n.t('foreign.business.sponsorship.label.birthplace')}
            className="foreign-business-sponsorship-birthplace"
            addressBooks={this.props.addressBooks}
            addressBook="ForeignNational"
            dispatch={this.props.dispatch}
            required={this.props.required}
            attribute={`${attribute}.Birthplace`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.sponsorship.heading.address')}
          optional
          help="foreign.business.sponsorship.help.address"
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Address`}
        >
          <Location
            name="Address"
            {...this.props.Address}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            className="foreign-business-sponsorship-address"
            layout={Location.ADDRESS}
            geocode
            showPostOffice
            addressBooks={this.props.addressBooks}
            addressBook="ForeignNational"
            dispatch={this.props.dispatch}
            required={this.props.required}
            attribute={`${attribute}.Address`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.sponsorship.heading.citizenship')}
          help="foreign.business.sponsorship.help.citizenship"
          adjustFor="country"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Citizenship`}
        >
          <Country
            name="Citizenship"
            {...this.props.Citizenship}
            onUpdate={this.updateCitizenship}
            onError={this.props.onError}
            className="foreign-business-sponsorship-citizenship"
            multiple
            required={this.props.required}
            attribute={`${attribute}.Citizenship`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.sponsorship.heading.organization')}
          adjustFor="text"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Organization`}
          exactAttribute
        >
          <NotApplicable
            name="OrganizationNotApplicable"
            {...this.props.OrganizationNotApplicable}
            onUpdate={this.updateOrganizationNotApplicable}
            onError={this.props.onError}
            or={i18n.m('foreign.business.sponsorship.para.or')}
            required={this.props.required}
            attribute={`${attribute}.OrganizationNotApplicable`}
          >
            <Text
              name="Organization"
              {...this.props.Organization}
              onUpdate={this.updateOrganization}
              onError={this.props.onError}
              className="foreign-business-sponsorship-organization"
              required={this.props.required}
              attribute={`${attribute}.Organization`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('foreign.business.sponsorship.heading.organizationaddress')}
          optional
          help="foreign.business.sponsorship.help.organizationaddress"
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.OrganizationAddress`}
        >
          <NotApplicable
            name="OrganizationAddressNotApplicable"
            {...this.props.OrganizationAddressNotApplicable}
            onUpdate={this.updateOrganizationAddressNotApplicable}
            onError={this.props.onError}
            or={i18n.m('foreign.business.sponsorship.para.or')}
            required={this.props.required}
            attribute={`${attribute}.OrganizationAddressNotApplicable`}
          >
            <Location
              name="OrganizationAddress"
              {...this.props.OrganizationAddress}
              onUpdate={this.updateOrganizationAddress}
              onError={this.props.onError}
              className="foreign-business-sponsorship-organizationaddress"
              layout={Location.ADDRESS}
              geocode
              addressBooks={this.props.addressBooks}
              addressBook="Organization"
              dispatch={this.props.dispatch}
              required={this.props.required}
              attribute={`${attribute}.OrganizationAddress`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('foreign.business.sponsorship.heading.dates')}
          help="foreign.business.sponsorship.help.dates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Dates`}
        >
          <DateRange
            name="Dates"
            {...this.props.Dates}
            onUpdate={this.updateDates}
            minDate={sponsorDOBMinDate}
            minDateEqualTo
            onError={this.props.onError}
            className="foreign-business-sponsorship-dates"
            required={this.props.required}
            prefix="foreignNationalSponsorship"
            hideFromDay
            hideToDay
            attribute={`${attribute}.Dates`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.sponsorship.heading.residence')}
          optional
          adjustFor="address no-buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Residence`}
        >
          <Location
            name="Residence"
            {...this.props.Residence}
            onUpdate={this.updateResidence}
            onError={this.props.onError}
            className="foreign-business-sponsorship-residence"
            disableToggle
            layout={Location.US_ADDRESS}
            geocode
            addressBooks={this.props.addressBooks}
            addressBook="ForeignNational"
            dispatch={this.props.dispatch}
            required={this.props.required}
            attribute={`${attribute}.Residence`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.sponsorship.heading.stay')}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Stay`}
        >
          <Textarea
            name="Stay"
            {...this.props.Stay}
            onUpdate={this.updateStay}
            onError={this.props.onError}
            className="foreign-business-sponsorship-stay"
            required={this.props.required}
            attribute={`${attribute}.Stay`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.sponsorship.heading.sponsorship')}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Sponsorship`}
        >
          <Textarea
            name="Sponsorship"
            {...this.props.Sponsorship}
            onUpdate={this.updateSponsorship}
            onError={this.props.onError}
            className="foreign-business-sponsorship-sponsorship"
            required={this.props.required}
            attribute={`${attribute}.Sponsorship`}
          />
        </Field>
      </div>
    );
  }
}

SponsorshipItem.defaultProps = {
  BirthdateNotApplicable: { applicable: true },
  OrganizationNotApplicable: { applicable: true },
  OrganizationAddressNotApplicable: { applicable: true },
  AddressNotApplicable: { applicable: true }, // PVQ Only
  WorkedWithPrivateOrganization: {},
  Reason: {}, // PVQ Only
  ReasonOtherExplanation: {}, // PVQ Only
  InUsaNotApplicable: { applicable: true }, // PVQ Only
  onUpdate: () => { },
  onError: (value, arr: ErrorCode[]) => arr,
};
